// src/components/leader/MediaUploadCard.js
//
// One card for every media file of a choreography (audit FL-05, FL-09, FL-10, FL-11,
// FL-30, FL-39): music, verification video and Grand Finále background video.
// * preview of the uploaded file (players only for formats browsers can play);
// * upload / replace through FileUploader + utils/upload.js (progress, cancel, Czech errors);
// * `editable={false}` shows the file read-only with the reason (`lockedReason`);
// * optional removal (background video).
// The verification video is checked for the DISCIPLINE only (organiser's rules: the league is
// not checked from it). The Grand Finále background video has its own hint (3584 × 1024 px,
// MP4, two weeks before the finale - `competitionDate` gives the exact day) and size advice.
import React, { useCallback, useState } from 'react';
import { Music2, Video, Film, Trash2, Download, Info, Lock } from 'lucide-react';
import config from '../../config';
import api, { getErrorMessage } from '../../services/api';
import { uploadFile, UPLOAD_MESSAGES, BACKGROUND_VIDEO_ADVICE } from '../../utils/upload';
import { backgroundVideoDeadline, formatDate } from '../../utils/format';
import FileUploader from '../layout/FileUploader';
import ConfirmDialog from '../ui/ConfirmDialog';
import { useToast } from '../ui/Toast';

const VIDEO_ACCEPT = 'video/mp4,video/quicktime,video/x-msvideo,.mp4,.mov,.avi';
const VIDEO_TIP =
  'Stačí krátká ukázka (1–2 minuty) v rozlišení 720p nebo 1080p. Video ve 4K je zbytečně velké a nahrává se velmi dlouho. ' +
  'Nahrávání velkého videa může trvat desítky minut – ideálně přes Wi-Fi.';

const KINDS = {
  music: {
    title: 'Hudba',
    Icon: Music2,
    optional: false,
    accept: 'audio/mpeg,.mp3',
    maxSizeMB: 100,
    uploadPath: (id) => `/choreographies/${id}/music`,
    mediaPath: (file) => `/choreographies/music/${encodeURIComponent(file)}`,
    hint:
      'Nahrajte hudbu k choreografii ve formátu MP3 (nejvýše 100 MB). Bez hudby nelze potvrdit prezenci. ' +
      'Záložní nahrávku si vezměte na soutěž na USB.',
    buttonText: 'Nahrát hudbu (MP3)',
    changeButtonText: 'Nahrát jinou hudbu',
    successText: 'Hudba je nahraná',
    missingText: 'Hudba zatím nebyla nahrána.',
  },
  verification: {
    title: 'Ověřovací video',
    Icon: Video,
    optional: true,
    accept: VIDEO_ACCEPT,
    maxSizeMB: 4096,
    uploadPath: (id) => `/choreographies/${id}/verification-video`,
    mediaPath: (file) => `/choreographies/verification-video/${encodeURIComponent(file)}`,
    hint:
      'Video z tréninku nebo z vystoupení, podle kterého organizátor ověří, že choreografie odpovídá zvolené disciplíně ' +
      `(ligu podle videa nekontroluje). ${VIDEO_TIP}`,
    buttonText: 'Nahrát video',
    changeButtonText: 'Nahrát jiné video',
    successText: 'Video je nahrané',
    missingText: 'Ověřovací video nebylo nahráno.',
    tooLargeMessage: UPLOAD_MESSAGES.videoTooLarge,
  },
  background: {
    title: 'Video pozadí pro Grand Finále',
    Icon: Film,
    optional: true,
    accept: VIDEO_ACCEPT,
    maxSizeMB: 4096,
    uploadPath: (id) => `/choreographies/${id}/background-video`,
    mediaPath: (file) => `/choreographies/background-video/${encodeURIComponent(file)}`,
    removePath: (id) => `/choreographies/${id}/background-video`,
    hint: ({ competitionDate }) => {
      const deadline = backgroundVideoDeadline({ date: competitionDate });
      return (
        'Video na pozadí pro Grand Finále: rozlišení 3584 × 1024 px, formát MP4. Nahrajte ho nejpozději 2 týdny před ' +
        `Grand Finále${deadline ? ` (do ${formatDate(deadline)})` : ''}. Pokud video nenahrajete, vybere ho náš kreativní tým.`
      );
    },
    buttonText: 'Nahrát video pozadí',
    changeButtonText: 'Nahrát jiné video',
    successText: 'Video pozadí je nahrané',
    missingText: 'Video pozadí nebylo nahráno.',
    tooLargeMessage: UPLOAD_MESSAGES.backgroundTooLarge,
    tooLargeHint: BACKGROUND_VIDEO_ADVICE,
  },
};

const extensionOf = (file) => (file && file.includes('.') ? file.split('.').pop().toLowerCase() : '');

function MediaPreview({ kind, file }) {
  const settings = KINDS[kind];
  const url = `${config.API_BASE_URL}${settings.mediaPath(file)}`;
  if (kind === 'music') {
    return (
      <audio controls preload="metadata" className="w-full" src={url}>
        Váš prohlížeč neumí přehrát zvuk. <a href={url}>Stáhnout hudbu</a>
      </audio>
    );
  }
  if (extensionOf(file) === 'mp4') {
    return (
      <video controls preload="metadata" playsInline className="max-h-80 w-full rounded-md bg-black" src={url}>
        Váš prohlížeč neumí video přehrát. <a href={url}>Stáhnout video</a>
      </video>
    );
  }
  // MOV (often HEVC from iPhones) and AVI rarely play in browsers - a black box would look broken.
  return (
    <div className="flex flex-col gap-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700 sm:flex-row sm:items-center sm:justify-between">
      <span>
        Video je nahrané ({extensionOf(file).toUpperCase() || 'video'}). Náhled tohoto formátu prohlížeč často neumí
        přehrát, organizátorům ale video dorazilo.
      </span>
      <a
        href={url}
        download
        className="inline-flex items-center gap-1 font-medium text-indigo-700 hover:text-indigo-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
      >
        <Download className="h-4 w-4" aria-hidden="true" />
        Stáhnout
      </a>
    </div>
  );
}

export default function MediaUploadCard({
  kind,
  choreographyId,
  file,
  editable = true,
  lockedReason,
  onUploaded,
  onRemoved,
  competitionDate,
}) {
  const settings = KINDS[kind];
  const hint = typeof settings.hint === 'function' ? settings.hint({ competitionDate }) : settings.hint;
  const toast = useToast();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState(null);

  const handleUpload = useCallback(
    async (selected, onProgress, signal) => {
      const result = await uploadFile(settings.uploadPath(choreographyId), selected, {
        onProgress,
        signal,
        tooLargeMessage: settings.tooLargeMessage,
      });
      if (onUploaded) onUploaded(result);
      return result;
    },
    [settings, choreographyId, onUploaded]
  );

  const closeRemove = useCallback(() => {
    setConfirmRemove(false);
    setRemoveError(null);
  }, []);

  const handleRemove = async () => {
    setRemoving(true);
    setRemoveError(null);
    try {
      await api.delete(settings.removePath(choreographyId));
      setConfirmRemove(false);
      toast.success('Video pozadí bylo odstraněno.');
      if (onRemoved) onRemoved();
    } catch (err) {
      setRemoveError(getErrorMessage(err));
    } finally {
      setRemoving(false);
    }
  };

  const { Icon } = settings;
  const inputId = `${kind}-upload-${choreographyId}`;

  return (
    <section className="rounded-lg bg-white p-4 shadow sm:p-6" aria-labelledby={`${inputId}-title`}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <h2 id={`${inputId}-title`} className="flex flex-wrap items-center gap-2 text-lg font-semibold text-gray-900">
          <Icon className="h-5 w-5 text-indigo-600" aria-hidden="true" />
          {settings.title}
          {settings.optional && <span className="text-xs font-normal text-gray-600">(nepovinné)</span>}
        </h2>
      </div>

      {editable && (
        <p className="mb-3 flex items-start gap-2 text-sm text-gray-600">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-500" aria-hidden="true" />
          <span>{hint}</span>
        </p>
      )}

      <div className="space-y-3">
        {file ? <MediaPreview kind={kind} file={file} /> : !editable && <p className="text-sm text-gray-600">{settings.missingText}</p>}

        {editable ? (
          <FileUploader
            id={inputId}
            label={settings.title}
            accept={settings.accept}
            maxSizeMB={settings.maxSizeMB}
            tooLargeHint={settings.tooLargeHint}
            onUpload={handleUpload}
            buttonText={settings.buttonText}
            changeButtonText={settings.changeButtonText}
            successText={settings.successText}
            currentFile={file}
          />
        ) : (
          lockedReason && (
            <p className="flex items-start gap-2 rounded-md bg-gray-50 p-3 text-sm text-gray-700">
              <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-500" aria-hidden="true" />
              <span>{lockedReason}</span>
            </p>
          )
        )}

        {editable && file && settings.removePath && (
          <button
            type="button"
            onClick={() => setConfirmRemove(true)}
            className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            Odstranit video pozadí
          </button>
        )}
      </div>

      {confirmRemove && (
        <ConfirmDialog
          title="Odstranit video pozadí?"
          message="Opravdu chcete video pozadí odstranit? Později ho můžete nahrát znovu."
          confirmLabel="Odstranit video"
          danger
          loading={removing}
          error={removeError}
          onConfirm={handleRemove}
          onCancel={closeRemove}
        />
      )}
    </section>
  );
}
